import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import authService from '../auth/authService';
import Navbar from './Navbar';
import './css/UserForm.css';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const UserForm = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    country: '',
    postal: '',
    phone: '',
    representing_company: false,
    company: '',
    nip_vat: '',
    position: '',
    company_adress: '',
    company_country: '',
    company_postal: ''
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const navigate = useNavigate();

  // Blokowanie scrollowania wszystkich scrollowalnych kontenerów gdy modal jest otwarty
  useEffect(() => {
    const scrollableEls = document.querySelectorAll('.lab-side-content, .user-form-container');
    if (showModal || showDeleteModal) {
      document.body.style.overflow = 'hidden';
      scrollableEls.forEach(el => { el.style.overflow = 'hidden'; });
    } else {
      document.body.style.overflow = '';
      scrollableEls.forEach(el => { el.style.overflow = ''; });
    }
    return () => {
      document.body.style.overflow = '';
      scrollableEls.forEach(el => { el.style.overflow = ''; });
    };
  }, [showModal, showDeleteModal]);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    if (currentUser) {
      authService.getUserProfile(currentUser.access)
        .then(response => {
          setFormData(prev => ({
            ...prev,
            ...response.data,
            representing_company: !!(response.data.company || response.data.nip_vat)
          }));
        })
        .catch(error => console.error(error));
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleModalSubmit = () => {
    if (!currentUser) return;
    const profileData = {
      ...formData,
      is_filled: !!(formData.name && formData.surname && formData.country)
    };
    authService.updateUserProfile(currentUser.access, profileData)
      .then(() => {
        const updatedUser = { ...currentUser, is_filled: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.location.reload();
      })
      .catch(error => console.error(error));
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const handleDeleteAccount = () => {
    if (!currentUser) return;
    authService.deleteUserProfile(currentUser.access, deletePassword)
      .then(() => {
        localStorage.removeItem('user');
        navigate('/login', {
          state: {
            message: 'Account has been terminated',
            messageType: 'success'
          }
        });
      })
      .catch(err => {
        const message = err?.response?.data?.detail || 'Something went wrong';
        setDeleteError(message);
      });
  };

  const submitPasswordChange = () => {
    if (!currentUser) return;
    authService.changePassword(currentUser.access, currentPassword, newPassword)
      .then(() => {
        setPasswordSuccess('Password changed successfully.');
        setPasswordError('');
        setCurrentPassword('');
        setNewPassword('');
        setTimeout(() => setShowPasswordChange(false), 1500);
      })
      .catch((err) => {
        const message = err?.response?.data?.detail || 'Failed to change password.';
        setPasswordError(message);
        setPasswordSuccess('');
      });
  };

  // Stały styl dla overlay – przykrywa całą stronę łącznie ze scrollowalnymi kontenerami
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    zIndex: 1000000,
  };

  return (
    <div className='lab-container'>
      <Navbar />
      <div className='lab-side-content'>
        <div className='user-form-container'>
          <h2 className='aval-endp mar-usr-prof'>Your profile</h2>
          <p className='complete mar-usr-prof'>Complete your profile information to unlock all features of the app.</p>

          <form onSubmit={handleSaveClick} className="form-user">
            <div className="user-sections-wrapper">
              <div className="user-section">
                <h3 className="user-section-title">Personal Information</h3>
                <input className="inp-user-field" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required />
                <input className="inp-user-field" name="surname" placeholder="Surname" value={formData.surname} onChange={handleChange} required />

                {currentUser && (
                  <input
                    className="inp-user-field readonly-field"
                    name="email"
                    placeholder="Email"
                    value={currentUser.email}
                    readOnly
                  />
                )}

                <input className="inp-user-field" name="country" placeholder="Country" value={formData.country} onChange={handleChange} required />
                <input className="inp-user-field" name="postal" placeholder="Postal Code" value={formData.postal} onChange={handleChange} />
                <input className="inp-user-field" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} />
              </div>

              <div className="user-section">
                <h3 className="user-section-title">Company Information</h3>
                <label className='checkbox-label'>
                  <input
                    type="checkbox"
                    name="representing_company"
                    checked={formData.representing_company}
                    onChange={handleChange}
                  />
                  Representing a Company
                </label>

                {formData.representing_company && (
                  <div className="company-fields">
                    <input className="inp-user-field" name="company" placeholder="Company Name" value={formData.company} onChange={handleChange} />
                    <input className="inp-user-field" name="nip_vat" placeholder="VAT/NIP" value={formData.nip_vat} onChange={handleChange} />
                    <input className="inp-user-field" name="position" placeholder="Position" value={formData.position} onChange={handleChange} />
                    <input className="inp-user-field" name="company_adress" placeholder="Company Address" value={formData.company_adress} onChange={handleChange} />
                    <input className="inp-user-field" name="company_country" placeholder="Company Country" value={formData.company_country} onChange={handleChange} />
                    <input className="inp-user-field" name="company_postal" placeholder="Company Postal Code" value={formData.company_postal} onChange={handleChange} />
                  </div>
                )}
              </div>
            </div>

            <div className="action-row">
              <button className='user-btn-main' type="submit">Save Profile</button>
            </div>
          </form>

          <hr className="user-divider" />

          <div className="account-settings-section">
            <h3 className="user-section-title">Account Settings</h3>
            <div className="settings-buttons-row">
              <button type="button" className="user-btn-secondary" onClick={() => setShowPasswordChange(!showPasswordChange)}>
                Change Password
              </button>
              <button type="button" className="user-btn-danger" onClick={() => setShowDeleteModal(true)}>
                <FontAwesomeIcon icon={faTrashCan} style={{ marginRight: '8px' }} />
                Delete Account
              </button>
            </div>

            {showPasswordChange && (
              <div className='password-change-box'>
                <input className="inp-user-field" type="password" placeholder="Current Password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                <input className="inp-user-field" type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />

                {passwordError && <p className="status-msg error">{passwordError}</p>}
                {passwordSuccess && <p className="status-msg success">{passwordSuccess}</p>}

                <div className="pass-btn-group">
                  <button
                    className="user-btn-main"
                    disabled={!currentPassword || !newPassword}
                    onClick={submitPasswordChange}
                    style={{ minWidth: '120px', height: '40px' }}
                  >
                    Submit
                  </button>
                  <button className="user-btn-cancel" onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && createPortal(
        <div style={overlayStyle}>
          <div className="modal-uf">
            <div className='conf-sub-acc'><h3>Confirm Your Information</h3></div>
            <ul className='save-acc-ul'>
              <li><strong>Name:</strong> {formData.name}</li>
              <li><strong>Surname:</strong> {formData.surname}</li>
              <li><strong>Country:</strong> {formData.country}</li>
              <li><strong>Postal:</strong> {formData.postal}</li>
              <li><strong>Phone:</strong> {formData.phone}</li>
              {formData.representing_company && (
                <>
                  <li><strong>Company:</strong> {formData.company}</li>
                  <li><strong>NIP/VAT:</strong> {formData.nip_vat}</li>
                  <li><strong>Position:</strong> {formData.position}</li>
                  <li><strong>Address:</strong> {formData.company_adress}</li>
                  <li><strong>Country:</strong> {formData.company_country}</li>
                  <li><strong>Postal:</strong> {formData.company_postal}</li>
                </>
              )}
            </ul>
            <div className="modal-buttons-uf">
              <button className='submit-acc-change' onClick={handleModalSubmit}>Submit</button>
              <button onClick={handleModalCancel}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showDeleteModal && createPortal(
        <div style={overlayStyle}>
          <div className="modal-uf">
            <div className='conf-del-acc'><h3>Confirm Account Deletion</h3></div>
            <p className='p-del-acc'>This action is irreversible. Please confirm your decision.</p>
            <div className='div-del-acc'>
              <label className='checkbox-label' style={{ justifyContent: 'center' }}>
                <input
                  type="checkbox"
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                />
                Yes, I really want to delete my account.
              </label>
              <input
                className='inp-user-field'
                style={{ marginTop: '15px' }}
                type="password"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
              />
            </div>
            {deleteError && <p style={{ color: 'red', textAlign: 'center' }}>{deleteError}</p>}

            <div className="modal-buttons-uf">
              <button disabled={!confirmDelete || !deletePassword} onClick={handleDeleteAccount}>
                Delete
              </button>
              <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserForm;
